import type { CrmStageProjection, CrmStageState } from '../types/crmStage.types';

export interface CrmStageDisplayRow {
  key: string;
  providerLabel: string | null;
  value: string;
  text: string;
  state: CrmStageState;
  statusLabel: 'Stale' | 'Refresh failed' | null;
}

function clean(value: string | null | undefined): string | null {
  const normalized = value?.trim();
  return normalized ? normalized : null;
}

/** REST handoffs use snake_case; GraphQL already returns camelCase. */
export function normalizeCrmStages(
  stages: readonly CrmStageProjection[] | null | undefined
): CrmStageProjection[] | undefined {
  return stages?.map((stage) => {
    const raw = stage as unknown as Record<string, unknown>;
    const field = (camel: string, snake: string) =>
      Object.prototype.hasOwnProperty.call(raw, camel) ? raw[camel] : raw[snake];
    return {
      connectionId: field('connectionId', 'connection_id') as string,
      providerId: field('providerId', 'provider_id') as string,
      providerLabel: field('providerLabel', 'provider_label') as string,
      stageId: field('stageId', 'stage_id') as string | null | undefined,
      stageLabel: field('stageLabel', 'stage_label') as string | null | undefined,
      state: stage.state,
      lastSuccessStatus: field('lastSuccessStatus', 'last_success_status') as
        string | null | undefined,
    };
  });
}

export function resolveLiveCrmStages(
  live: { id: string; crmStages?: CrmStageProjection[] } | null | undefined,
  snapshot: { id: string; crmStages?: CrmStageProjection[] } | null | undefined
): CrmStageProjection[] | undefined {
  if (live && (!snapshot || live.id === snapshot.id)) {
    return live.crmStages ?? snapshot?.crmStages;
  }
  return snapshot?.crmStages;
}

function lastGoodValue(stage: CrmStageProjection): string {
  const label = clean(stage.stageLabel);
  if (label) return label;
  return stage.lastSuccessStatus === 'missing' ? 'N/A' : 'Not synced';
}

function stageValue(stage: CrmStageProjection): string {
  switch (stage.state) {
    case 'available':
      return clean(stage.stageLabel) ?? 'Not synced';
    case 'missing':
      return 'N/A';
    case 'stale':
    case 'failed_refresh':
      return lastGoodValue(stage);
    case 'loading':
      return 'Loading…';
    case 'never_synced':
    default:
      return 'Not synced';
  }
}

/**
 * Produces provider-neutral display rows. The API already orders projections,
 * but sorting defensively keeps every surface stable if a REST cache or older
 * client supplies the same records in a different order.
 */
export function buildCrmStageDisplayRows(
  stages: readonly CrmStageProjection[] | null | undefined
): CrmStageDisplayRow[] {
  if (!stages?.length) {
    return [
      {
        key: 'not-synced',
        providerLabel: null,
        value: 'Not synced',
        text: 'Not synced',
        state: 'never_synced',
        statusLabel: null,
      },
    ];
  }

  return [...stages]
    .sort((left, right) => {
      const leftKey = `${clean(left.providerLabel)?.toLowerCase() ?? ''}\u0000${left.providerId}\u0000${left.connectionId}`;
      const rightKey = `${clean(right.providerLabel)?.toLowerCase() ?? ''}\u0000${right.providerId}\u0000${right.connectionId}`;
      return leftKey < rightKey ? -1 : leftKey > rightKey ? 1 : 0;
    })
    .map((stage) => {
      const providerLabel = clean(stage.providerLabel);
      const value = stageValue(stage);
      return {
        key: stage.connectionId || `${stage.providerId}:${stage.stageId ?? stage.state}`,
        providerLabel,
        value,
        text: providerLabel ? `${providerLabel} · ${value}` : value,
        state: stage.state,
        statusLabel:
          stage.state === 'stale'
            ? 'Stale'
            : stage.state === 'failed_refresh'
              ? 'Refresh failed'
              : null,
      };
    });
}

export function crmStageAccessibleSummary(rows: readonly CrmStageDisplayRow[]): string {
  return rows
    .map((row) => (row.statusLabel ? `${row.text} (${row.statusLabel})` : row.text))
    .join(', ');
}

type CrmConnectionLike = { app?: string | null; crmType?: string | null; status?: string | null };

/** CRMs whose stage sync is switched on (remi-sync `supports_stages`). */
const CRM_STAGE_APPS = new Set([
  'follow-up-boss',
  'cinc',
  'lofty',
  'sierra-interactive',
  'boldtrail',
  'boldtrail-front-office',
]);
const CRM_STAGE_LEGACY_TYPES = new Set(['followupboss', 'cinc']);

/**
 * SPEC-2783/2812/2813/2814/2815: CRM Stage is shown only when the org's
 * current connection is an active one to a CRM whose stage sync is on
 * (FUB, CINC, Lofty, Sierra Interactive, BoldTrail).
 * Shared FUB team accounts carry the parent's app. Paused, disconnected or
 * other-CRM orgs would only ever see "Not synced". Uses the org's connection,
 * not each lead's source.
 */
export function orgShowsCrmStage(
  crmConnections: readonly CrmConnectionLike[] | null | undefined
): boolean {
  return (crmConnections ?? []).some((connection) => {
    const status = (connection.status ?? '').toLowerCase();
    const supportsStages = connection.app
      ? CRM_STAGE_APPS.has(connection.app)
      : CRM_STAGE_LEGACY_TYPES.has(connection.crmType ?? '');
    return supportsStages && (status === 'active' || status === 'connected');
  });
}

/** Order-stable identity of a stage list, across REST and GraphQL shapes. */
export function crmStagesKey(
  stages: readonly CrmStageProjection[] | null | undefined
): string {
  return JSON.stringify(normalizeCrmStages(stages) ?? null);
}

/**
 * Handoff detail is fetched once per card and cached while the list keeps
 * refreshing. Once the list has changed after the detail arrived, the list
 * is the fresher source and keeps that authority until a new detail result
 * arrives, so a stale (or empty) cached detail can never come back.
 */
export function resolveHandoffCrmStages(
  listStages: readonly CrmStageProjection[] | null | undefined,
  detailStages: readonly CrmStageProjection[] | null | undefined,
  listSupersededDetail: boolean
): CrmStageProjection[] | undefined {
  if (detailStages == null || listSupersededDetail) return normalizeCrmStages(listStages);
  return normalizeCrmStages(detailStages);
}
